import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, BookOpen, Compass, ChevronRight, Volume2, VolumeX, ArrowRight, Briefcase, Users, Globe, ShoppingBag, GraduationCap, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VideoModal, VideoCard } from '@/components/VideoModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6a4505e5-3f70-4447-9214-c7fe0572ffa6/artifacts/4ongzfvs_THE%20MATRIX%20UNIVERSITY%20VIRTUAL%20REALITY%20LOGO.png";
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/gqu318lf_PUBLIC%20-%20The%20Matrix%20University%20-%2047%2C000%20Sim%20Lab.mp4";

/* ─── VR Headset Intro Animation ─── */
const VRHeadsetIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState(0); // 0=headset fly-in, 1=visor glow, 2=logo reveal, 3=flash, 4=done

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 1200),   // visor starts glowing
      setTimeout(() => setPhase(2), 2200),   // logo assembles
      setTimeout(() => setPhase(3), 3800),   // flash burst
      setTimeout(() => { setPhase(4); onComplete(); }, 4600), // done
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  if (phase === 4) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: '#050a14' }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Scanlines */}
      <div className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(56,189,248,0.03) 2px, rgba(56,189,248,0.03) 4px)',
        }}
      />

      {/* VR Headset SVG */}
      <motion.div
        initial={{ scale: 3, opacity: 0 }}
        animate={{
          scale: phase >= 1 ? 1 : 1.5,
          opacity: phase >= 3 ? 0 : 1,
        }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        className="relative"
      >
        <svg width="360" height="220" viewBox="0 0 360 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-2xl">
          {/* Headset body */}
          <rect x="30" y="40" width="300" height="140" rx="40" fill="#111827"
            stroke={phase >= 1 ? 'rgba(124,58,237,0.6)' : 'rgba(255,255,255,0.1)'}
            strokeWidth="2"
            className="transition-all duration-700"
          />
          {/* Left lens */}
          <ellipse cx="130" cy="110" rx="65" ry="50"
            fill={phase >= 1 ? 'rgba(124,58,237,0.15)' : 'rgba(0,0,0,0.5)'}
            stroke={phase >= 1 ? 'rgba(56,189,248,0.5)' : 'rgba(255,255,255,0.08)'}
            strokeWidth="2"
            className="transition-all duration-700"
          />
          {/* Right lens */}
          <ellipse cx="230" cy="110" rx="65" ry="50"
            fill={phase >= 1 ? 'rgba(124,58,237,0.15)' : 'rgba(0,0,0,0.5)'}
            stroke={phase >= 1 ? 'rgba(56,189,248,0.5)' : 'rgba(255,255,255,0.08)'}
            strokeWidth="2"
            className="transition-all duration-700"
          />
          {/* Nose bridge */}
          <path d="M165 130 Q180 155 195 130" stroke="rgba(255,255,255,0.1)" strokeWidth="2" fill="none" />
          {/* Strap hints */}
          <rect x="0" y="80" width="30" height="60" rx="10" fill="#111827" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          <rect x="330" y="80" width="30" height="60" rx="10" fill="#111827" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          {/* Top accent */}
          <rect x="140" y="38" width="80" height="6" rx="3"
            fill={phase >= 1 ? 'rgba(251,191,36,0.6)' : 'rgba(255,255,255,0.05)'}
            className="transition-all duration-500"
          />
        </svg>

        {/* Visor glow effect */}
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.8, 0.5, 0.8] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.2) 0%, transparent 70%)',
            }}
          />
        )}

        {/* Logo reveal inside visor */}
        {phase >= 2 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="absolute inset-0 flex items-center justify-center"
          >
            {/* Glitch layers */}
            <div className="relative">
              <motion.img
                src={LOGO_URL}
                alt="Matrix University"
                className="w-28 h-28 object-contain"
                animate={{
                  x: [0, -3, 2, -1, 0],
                  filter: [
                    'hue-rotate(0deg) brightness(1)',
                    'hue-rotate(90deg) brightness(1.5)',
                    'hue-rotate(-30deg) brightness(1.2)',
                    'hue-rotate(0deg) brightness(1)',
                  ],
                }}
                transition={{ duration: 0.8, times: [0, 0.2, 0.5, 1] }}
                style={{ filter: 'drop-shadow(0 0 20px rgba(124,58,237,0.8)) drop-shadow(0 0 40px rgba(56,189,248,0.5))' }}
              />
              {/* Glitch clone - red channel */}
              <motion.img
                src={LOGO_URL}
                alt=""
                className="w-28 h-28 object-contain absolute top-0 left-0 mix-blend-screen"
                initial={{ opacity: 0.7, x: 4 }}
                animate={{ opacity: [0.7, 0, 0.3, 0], x: [4, -2, 3, 0] }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                style={{ filter: 'hue-rotate(120deg)' }}
              />
              {/* Glitch clone - blue channel */}
              <motion.img
                src={LOGO_URL}
                alt=""
                className="w-28 h-28 object-contain absolute top-0 left-0 mix-blend-screen"
                initial={{ opacity: 0.5, x: -3 }}
                animate={{ opacity: [0.5, 0, 0.2, 0], x: [-3, 2, -1, 0] }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                style={{ filter: 'hue-rotate(-90deg)' }}
              />
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Flash burst */}
      {phase >= 3 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.9), rgba(124,58,237,0.3) 40%, transparent 70%)',
          }}
        />
      )}

      {/* "Entering The Matrix" text */}
      {phase >= 1 && phase < 3 && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="absolute bottom-20 text-sm tracking-[0.3em] text-cyan-400/60 uppercase"
        >
          Entering The Matrix
        </motion.p>
      )}
    </motion.div>
  );
};

/* ─── Hero Video Player ─── */
const HeroVideoPlayer = () => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.6 }}
      className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden glass-light"
      data-testid="hero-video"
    >
      <div className="relative aspect-video bg-black">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20 z-10">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <video
          ref={videoRef}
          src={HERO_VIDEO_URL}
          className="w-full h-full object-cover"
          autoPlay muted={isMuted} loop playsInline
          onCanPlay={() => setIsLoading(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
        <div className="absolute top-4 right-4 flex gap-2 z-20">
          <button onClick={() => { if (videoRef.current) { videoRef.current.muted = !videoRef.current.muted; setIsMuted(!isMuted); } }}
            className="w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="hero-mute-btn">
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <button onClick={() => { if (videoRef.current) { isPlaying ? videoRef.current.pause() : videoRef.current.play(); } }}
            className="w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="hero-play-btn">
            {isPlaying ? <span className="flex gap-1"><span className="w-1.5 h-4 bg-white rounded-sm" /><span className="w-1.5 h-4 bg-white rounded-sm" /></span> : <Play className="w-5 h-5 fill-white" />}
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        <div className="absolute bottom-4 left-4 z-20"><p className="text-white font-semibold text-sm">$47,000 Simulation Lab Experience</p></div>
      </div>
    </motion.div>
  );
};

/* ─── Quick Link Card ─── */
const QuickLinkCard = ({ to, icon: Icon, title, desc, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay }}
  >
    <Link to={to} className="block p-5 rounded-xl glass-light tech-bracket card-hover group" data-testid={`quick-link-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-11 h-11 rounded-lg ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="text-white font-semibold mb-1 group-hover:text-cta transition-colors">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
    </Link>
  </motion.div>
);

/* ─── Main HomePage ─── */
export default function HomePage() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [introComplete, setIntroComplete] = useState(false);
  const [settings, setSettings] = useState({
    hero_title: "The Matrix University – Where Education Meets the Future",
    hero_subtitle: "Immersive education through VR, AI, culture, and global learning."
  });

  useEffect(() => {
    axios.get(`${API}/videos?featured=true&page=home`).then(res => setVideos(res.data)).catch(console.error);
    axios.get(`${API}/settings`).then(res => setSettings(prev => ({ ...prev, ...res.data }))).catch(console.error);
    axios.post(`${API}/seed`).catch(() => {});

    // Check if intro was already shown this session
    if (sessionStorage.getItem('matrixIntroShown')) {
      setIntroComplete(true);
    }
  }, []);

  const handleIntroComplete = () => {
    setIntroComplete(true);
    sessionStorage.setItem('matrixIntroShown', 'true');
  };

  const quickLinks = [
    { to: '/services', icon: Mic, title: 'Services', desc: 'Keynote, immersive experiences & education programs', color: 'bg-primary/30' },
    { to: '/courses', icon: GraduationCap, title: 'Memberships', desc: 'VR headset + VictoryXR — starting at $500/year', color: 'bg-secondary/30' },
    { to: '/international-reseller', icon: Globe, title: 'Reseller', desc: 'K-12 & Higher Ed immersive classroom bundles', color: 'bg-green-600/30' },
    { to: '/sponsorship', icon: Users, title: 'Sponsorship', desc: 'Community, Innovation & Legacy tiers', color: 'bg-cta/20' },
    { to: '/merchandise', icon: ShoppingBag, title: 'Merchandise', desc: 'Official Matrix University apparel', color: 'bg-rose-600/30' },
    { to: '/donate', icon: Briefcase, title: 'Donate', desc: 'Support immersive education for all', color: 'bg-amber-600/30' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* VR Headset Intro Animation */}
      <AnimatePresence>
        {!introComplete && <VRHeadsetIntro onComplete={handleIntroComplete} />}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-30" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(124, 58, 237, 0.3) 2px, transparent 0)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 container-custom pt-24 pb-16">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo */}
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="mb-8">
              <img src={LOGO_URL} alt="The Matrix University" className="w-48 h-48 md:w-64 md:h-64 mx-auto object-contain drop-shadow-[0_0_15px_rgba(139,92,246,0.5)]" data-testid="hero-logo" />
            </motion.div>

            {/* Title */}
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight text-glow" data-testid="hero-title">
              The Matrix University
            </motion.h1>

            {/* Credentials */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mb-6">
              <h2 className="text-xl md:text-2xl text-secondary font-semibold mb-2" data-testid="hero-name">Dr. Jason E. Williams, Ed.D.</h2>
              <p className="text-muted-foreground" data-testid="hero-credential">Doctor of Education in Organizational Innovation</p>
            </motion.div>

            {/* Tagline */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mb-4">
              <p className="text-xl md:text-2xl text-cta font-semibold" data-testid="hero-tagline">Education • Innovation • Immersive Technology</p>
            </motion.div>

            {/* Hero Video */}
            <div className="mb-10"><HeroVideoPlayer /></div>

            {/* Subtitle */}
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }}
              className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Keynote speaking, immersive experiences, VR education, esports, and scalable technology solutions for schools, organizations, and communities worldwide.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/services" className="btn-primary flex items-center gap-2" data-testid="cta-services">
                <Briefcase className="w-5 h-5" /> Explore Services
              </Link>
              <Link to="/courses" className="btn-secondary flex items-center gap-2" data-testid="cta-memberships">
                <GraduationCap className="w-5 h-5" /> View Memberships
              </Link>
              <Link to="/contact" className="btn-secondary flex items-center gap-2" data-testid="cta-contact">
                <ChevronRight className="w-5 h-5" /> Contact Us
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-white" />
          </div>
        </motion.div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-card/50" data-testid="quick-links-section">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-3">What We Offer</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">From keynote speaking to full immersive education programs — explore everything The Matrix University provides.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {quickLinks.map((link, i) => <QuickLinkCard key={link.to} {...link} delay={i * 0.08} />)}
          </div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-20" data-testid="featured-videos-section">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">Featured Videos</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Experience our immersive educational content.</p>
          </motion.div>
          <div className="max-w-2xl mx-auto">
            {videos.map((video, index) => <VideoCard key={video.id} video={video} onClick={setSelectedVideo} index={index} />)}
          </div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-10">
            <Link to="/media" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-semibold" data-testid="view-all-videos">
              View All Videos <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* VR Campus Preview */}
      <section className="py-20 bg-card/50" data-testid="vr-preview-section">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">Explore Our VR Campus</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Step inside The Matrix University virtual reality campus.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative rounded-2xl overflow-hidden glass-light">
            <div className="aspect-video relative">
              <img src="https://static.prod-images.emergentagent.com/jobs/6a4505e5-3f70-4447-9214-c7fe0572ffa6/images/6a0f92298b626608a0fb990af56985ffdec330a4d04d8c70ecf0b397b11124a7.png"
                alt="Students wearing Meta Quest VR headsets" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <Link to="/vr-campus" className="inline-flex items-center gap-2 btn-primary" data-testid="vr-preview-cta">
                  <Compass className="w-5 h-5" /> Explore the VR Campus <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">Ready to Experience the Future of Education?</h2>
            <p className="text-xl text-muted-foreground mb-8">Join schools, organizations, and communities transforming learning through immersive technology.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services" className="btn-primary" data-testid="cta-services-bottom">Explore Services</Link>
              <Link to="/contact" className="btn-secondary" data-testid="cta-contact-bottom">Get in Touch</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-card/30" data-testid="partners-section">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3">Our Partners</h2>
            <p className="text-muted-foreground mb-8">Proud to collaborate with industry leaders in VR education</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              <a href="https://www.victoryxr.com" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity" data-testid="partner-victoryxr">
                <img src="https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/h02r640c_IMG_4656.jpeg" alt="VictoryXR" className="h-20 md:h-24 w-auto rounded-lg" />
              </a>
              <a href="https://www.meta.com" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity" data-testid="partner-meta">
                <img src="https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/4l2csir8_IMG_4658.jpeg" alt="Meta" className="h-20 md:h-24 w-auto rounded-lg" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal isOpen={!!selectedVideo} onClose={() => setSelectedVideo(null)} videoId={selectedVideo?.youtube_id} videoUrl={selectedVideo?.youtube_url} title={selectedVideo?.title} />
    </div>
  );
}
